import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiveModal, setReceiveModal] = useState(false);
  const [receiveData, setReceiveData] = useState({});

  useEffect(() => {
    fetchPurchaseOrder();
  }, [id]);

  const fetchPurchaseOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}purchase-orders/${id}`);
      setPurchaseOrder(response.data.data);
    } catch (error) {
      toast.error('Error fetching purchase order');
      history.push('/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      let endpoint = '';
      let message = '';

      switch (newStatus) {
        case 'submit':
          endpoint = `${process.env.REACT_APP_API_URL}purchase-orders/${id}/submit`;
          message = 'Purchase order submitted successfully';
          break;
        case 'approve':
          endpoint = `${process.env.REACT_APP_API_URL}purchase-orders/${id}/approve`;
          message = 'Purchase order approved successfully';
          break;
        case 'cancel':
          endpoint = `${process.env.REACT_APP_API_URL}purchase-orders/${id}/cancel`;
          message = 'Purchase order cancelled successfully';
          break;
        default:
          return;
      }

      await axios.post(endpoint);
      toast.success(message);
      fetchPurchaseOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleReceive = async () => {
    try {
      const items = Object.keys(receiveData).map(itemId => ({
        purchase_order_item_id: parseInt(itemId),
        quantity_received: receiveData[itemId]
      })).filter(item => item.quantity_received > 0);

      if (items.length === 0) {
        toast.warning('Please enter quantities to receive');
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_URL}purchase-orders/${id}/receive`, { items });
      toast.success('Items received successfully');
      setReceiveModal(false);
      setReceiveData({});
      fetchPurchaseOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error receiving items');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      draft: 'bg-secondary',
      pending: 'bg-warning',
      approved: 'bg-info',
      ordered: 'bg-primary',
      partially_received: 'bg-warning',
      received: 'bg-success',
      cancelled: 'bg-danger'
    };

    const statusLabels = {
      draft: 'Draft',
      pending: 'Pending Approval',
      approved: 'Approved',
      ordered: 'Ordered',
      partially_received: 'Partially Received',
      received: 'Received',
      cancelled: 'Cancelled'
    };

    return (
      <span className={`badge ${statusColors[status] || 'bg-secondary'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const canReceive = () => {
    return ['ordered', 'partially_received'].includes(purchaseOrder?.status);
  };

  const canEdit = () => {
    return purchaseOrder?.status === 'draft';
  };

  if (loading) {
    return <div className="text-center">Loading...</div>;
  }

  if (!purchaseOrder) {
    return <div className="text-center">Purchase order not found</div>;
  }

  return (
    <div className="purchase-order-detail">
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">
            Purchase Order: {purchaseOrder.po_number}
          </h4>
          <div>
            {canEdit() && (
              <button
                type="button"
                className="btn btn-warning me-2"
                onClick={() => history.push(`/purchase-orders/${id}/edit`)}
              >
                Edit
              </button>
            )}
            {purchaseOrder.status === 'draft' && (
              <button
                type="button"
                className="btn btn-success me-2"
                onClick={() => handleStatusChange('submit')}
              >
                Submit for Approval
              </button>
            )}
            {purchaseOrder.status === 'pending' && (
              <button
                type="button"
                className="btn btn-success me-2"
                onClick={() => handleStatusChange('approve')}
              >
                Approve
              </button>
            )}
            {canReceive() && (
              <button
                type="button"
                className="btn btn-info me-2"
                onClick={() => setReceiveModal(true)}
              >
                Receive Items
              </button>
            )}
            {['draft', 'pending', 'approved'].includes(purchaseOrder.status) && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleStatusChange('cancel')}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h5>Order Information</h5>
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td><strong>PO Number:</strong></td>
                    <td>{purchaseOrder.po_number}</td>
                  </tr>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>{getStatusBadge(purchaseOrder.status)}</td>
                  </tr>
                  <tr>
                    <td><strong>Order Date:</strong></td>
                    <td>{new Date(purchaseOrder.order_date).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <td><strong>Expected Delivery:</strong></td>
                    <td>{new Date(purchaseOrder.expected_delivery_date).toLocaleDateString()}</td>
                  </tr>
                  <tr>
                    <td><strong>Currency:</strong></td>
                    <td>{purchaseOrder.currency}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <h5>Supplier Information</h5>
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td><strong>Name:</strong></td>
                    <td>{purchaseOrder.supplier_name}</td>
                  </tr>
                  <tr>
                    <td><strong>Email:</strong></td>
                    <td>{purchaseOrder.supplier_email}</td>
                  </tr>
                  <tr>
                    <td><strong>Phone:</strong></td>
                    <td>{purchaseOrder.supplier_phone}</td>
                  </tr>
                  <tr>
                    <td><strong>Address:</strong></td>
                    <td>{purchaseOrder.supplier_address}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-md-12">
              <h5>Order Items</h5>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Quantity Ordered</th>
                      <th>Quantity Received</th>
                      <th>Pending</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrder.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.product_name}</td>
                        <td>{item.product_sku}</td>
                        <td>{item.quantity_ordered}</td>
                        <td>{item.quantity_received}</td>
                        <td>{item.quantity_ordered - item.quantity_received}</td>
                        <td>${item.unit_price}</td>
                        <td>${item.total_price}</td>
                        <td>
                          {item.quantity_received === 0 ? (
                            <span className="badge bg-warning">Pending</span>
                          ) : item.quantity_received >= item.quantity_ordered ? (
                            <span className="badge bg-success">Received</span>
                          ) : (
                            <span className="badge bg-info">Partial</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-md-6">
              <h5>Notes</h5>
              <p>{purchaseOrder.notes || 'No notes available'}</p>
            </div>
            <div className="col-md-6">
              <h5>Terms & Conditions</h5>
              <p>{purchaseOrder.terms_conditions || 'No terms specified'}</p>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <h5>Order Summary</h5>
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td><strong>Subtotal:</strong></td>
                    <td className="text-end">${purchaseOrder.subtotal}</td>
                  </tr>
                  <tr>
                    <td><strong>Tax Amount:</strong></td>
                    <td className="text-end">${purchaseOrder.tax_amount}</td>
                  </tr>
                  <tr>
                    <td><strong>Discount Amount:</strong></td>
                    <td className="text-end">-${purchaseOrder.discount_amount}</td>
                  </tr>
                  <tr>
                    <td><strong>Shipping Amount:</strong></td>
                    <td className="text-end">${purchaseOrder.shipping_amount}</td>
                  </tr>
                  <tr>
                    <td><strong>Total Amount:</strong></td>
                    <td className="text-end"><strong>${purchaseOrder.total_amount}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => history.push('/purchase-orders')}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>

      {/* Receive Items Modal */}
      {receiveModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Receive Items</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setReceiveModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  Enter the quantities received for each item. Leave blank for items not received.
                </div>
                
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Ordered</th>
                        <th>Already Received</th>
                        <th>Pending</th>
                        <th>Quantity to Receive</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrder.items
                        .filter(item => item.quantity_ordered > item.quantity_received)
                        .map((item, index) => {
                          const pending = item.quantity_ordered - item.quantity_received;
                          return (
                            <tr key={index}>
                              <td>{item.product_name}</td>
                              <td>{item.quantity_ordered}</td>
                              <td>{item.quantity_received}</td>
                              <td>{pending}</td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  max={pending}
                                  className="form-control"
                                  value={receiveData[item.purchase_order_item_id] || ''}
                                  onChange={(e) => setReceiveData(prev => ({
                                    ...prev,
                                    [item.purchase_order_item_id]: parseInt(e.target.value) || 0
                                  }))}
                                  placeholder="0"
                                />
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleReceive}
                >
                  Receive Items
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setReceiveModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetail; 